"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { runAction } from "@/lib/actions/client";
import { getOrCreateMyOrgAction } from "@/lib/orgs/actions";
import { startCheckoutAction } from "@/lib/billing/actions";

type BuyButtonProps = {
  productKey: string;
  children: React.ReactNode;
  /** Seat-priced tiers: show a quantity picker and pass it to checkout. */
  withSeatPicker?: boolean;
};

/**
 * Buy → ensure the caller has an org (personal one auto-created) → Polar
 * checkout redirect. Signed-out users are sent to login and come back here.
 */
export function BuyButton({ productKey, children, withSeatPicker = false }: BuyButtonProps) {
  const router = useRouter();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [seats, setSeats] = useState(5);

  async function buy() {
    setError(null);
    setPending(true);
    try {
      const org = await runAction(getOrCreateMyOrgAction, {});
      if (!org.ok) {
        if (org.error._tag === "UnauthorizedError" || org.error._tag === "NotAuthenticated") {
          router.push("/login?next=/pricing");
          return;
        }
        setError("Could not prepare your account. Try again.");
        return;
      }
      const checkout = await runAction(startCheckoutAction, {
        org_id: org.value.id,
        product_key: productKey,
        seats: withSeatPicker ? seats : undefined,
      });
      if (!checkout.ok) {
        setError("Checkout could not be started. Try again.");
        return;
      }
      window.location.assign(checkout.value.checkout_url);
    } finally {
      setPending(false);
    }
  }

  return (
    <div>
      {withSeatPicker && (
        <label className="mb-3 flex items-center justify-between text-sm text-[var(--muted)]">
          Seats
          <input
            type="number"
            min={1}
            max={500}
            value={seats}
            onChange={(e) => setSeats(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
            className="w-24 rounded-xl border border-white/15 bg-white/[0.04] px-3 py-1.5 text-right text-sm text-white outline-none transition focus:border-fuchsia-400/50"
          />
        </label>
      )}
      <button
        type="button"
        onClick={buy}
        disabled={pending}
        className="w-full rounded-full border border-fuchsia-400/30 bg-fuchsia-400/10 px-6 py-2.5 text-sm font-medium text-fuchsia-100 transition hover:bg-fuchsia-400/20 disabled:opacity-50"
      >
        {pending ? "Preparing checkout…" : children}
      </button>
      {error && (
        <p role="alert" className="mt-2 text-center text-sm text-rose-300">
          {error}
        </p>
      )}
    </div>
  );
}
